import React, { forwardRef } from 'react'
import { css } from '@emotion/core'
import { BillboardButton, useTheme } from '@fs/zion-ui'
import { isExternalUrl } from '../helpers/urlHelpers'
import { navConfig } from '../../config'

type PillButtonSize = 'sm' | 'md' | 'lg'

type BillboardButtonProps = React.ComponentProps<typeof BillboardButton>

type PillButtonProps = Omit<BillboardButtonProps, 'size'> & {
  size?: PillButtonSize
}

const buttonHackCss = css`
  & {
    button,
    a {
      border-radius: 2em;
      text-transform: none;
      font-weight: 600;
      font-size: var(--text-size-b);
      white-space: normal !important;
      overflow: visible;
      word-break: unset;
      max-width: fit-content;
      text-align: center;
      height: auto;
      padding-bottom: 13px;
      overflow: hidden;
    }
  }

  &[data-bigness='sm'] {
    button,
    a {
      display: block !important;
    }
  }
`

const PillButton = forwardRef<HTMLElement, PillButtonProps>(
  ({ emphasis = 'high', size = 'lg', children, to, color, ...props }, ref) => {
    const theme = useTheme()
    const toUrl = typeof to === 'string' ? to : undefined
    const external = toUrl ? isExternalUrl(toUrl) : false

    // HACK: we failed to add an exeption for links that go to *.familysearch.org that are not hosted by familysearch.
    const shouldPrependLangCode = navConfig.langCodeBlackList.some((langCode) => toUrl?.includes(langCode))

    // This is a little trick to only send the prop if it is defined. Not sure why it still shows in the DOM when undefined, but I guess this'll work...
    const noPrependLocaleCode = shouldPrependLangCode ? { noPrependLocaleCode: true } : {}

    const themedColor = color || (theme === 'day' && emphasis === 'low' && 'blue') || 'gray'

    return (
      <div css={buttonHackCss} data-bigness={size}>
        <BillboardButton
          emphasis={emphasis}
          external={to ? external : undefined}
          target={external ? '_blank' : '_self'}
          to={to}
          color={themedColor}
          {...noPrependLocaleCode}
          {...props}
          ref={ref}
        >
          {children}
        </BillboardButton>
      </div>
    )
  }
)

export default PillButton
